/* LAV TDMS — CRS / ACSR comment register (threaded). */
import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import {
  Button,
  Badge,
  CodeChip,
  Avatar,
  Field,
  Toolbar,
  PageHead,
  EmptyState,
  Input,
  Select, DateInput, toast, clickable, railWidth, Spinner } from '@/ui'
import { exportRows } from '@/lib/excel'
import { Icon } from '@/ds/icons'
import { qk, useItem, useList, useSub } from '@/data/queries'
import { fetchList } from '@/data/api'
import { useCommand } from '@/data/mutations'
import { useDictRows, useMe } from '@/data/meta'
import { SheetPrint } from '@/screens/pt/CrsSheet'
import type { Crs, CritLevel, ReviewCode, Vendor } from '@/data/types'
import { revVer } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'
import { CrsFilesZone } from '@/screens/pt/TrmFiles'


const CRIT: Record<CritLevel, { tone: 'danger' | 'warning' | 'neutral'; ru: string }> = {
  high: { tone: 'danger', ru: 'Высокая' },
  med: { tone: 'warning', ru: 'Средняя' },
  low: { tone: 'neutral', ru: 'Низкая' },
}

// Prototype's controlled underlined tab strip (Tabs DS component).
function TabStrip({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: string; label: ReactNode; count?: number }[]
  value: string
  onChange: (id: string) => void
}) {
  return (
    <div
      role="tablist"
      style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: 'var(--border-hairline) solid var(--border-default)' }}
    >
      {tabs.map((tb) => {
        const active = tb.id === value
        return (
          <button
            key={tb.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tb.id)}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-5) var(--space-5)',
              marginBottom: -1,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              font: 'var(--font-ui)',
              fontSize: 'var(--text-md)',
              fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
              color: active ? 'var(--text-strong)' : 'var(--text-muted)',
              borderBottom: `var(--border-accent) solid ${active ? 'var(--accent)' : 'transparent'}`,
              // Подчёркивание переходит вместе с цветом: порознь текст
              // перетекает, а полоса прыгает.
              transition: 'color var(--duration-fast) var(--ease-standard), border-bottom-color var(--duration-fast) var(--ease-standard)',
            }}
          >
            {tb.label}
            {tb.count != null ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 'var(--weight-medium)',
                  color: active ? 'var(--accent-ink)' : 'var(--text-subtle)',
                  background: active ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                  border: `var(--border-hairline) solid ${active ? 'var(--accent-line)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '1px var(--space-3)',
                }}
              >
                {tb.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/** Размер страницы. Сервер отдаёт ровно эту порцию. */
const PAGE_SIZE = 200

/** Строка реестра листов — ответ GET /api/crs-sheets. */
export interface CrsSheetRow {
  id: string
  doc: string
  title: string | null
  disc: string | null
  rev: string
  /** Подача, по которой лист выпущен: та же, что у его замечаний. */
  ver?: number | null
  code: ReviewCode
  status: 'draft' | 'issued'
  trmIn: string | null
  trmOut: string | null
  items: number
  open: number
  /** Отработан: замечаний без ответа не осталось. Считает сервер по всему набору. */
  settled: boolean
  /** Есть ответы, ждущие решения заказчика: закрыть или вернуть. */
  toDecide: boolean
  preparedBy: string
  date: string | null
}

/* Дерево листов «документ → листы CRS» — одно на два экрана: заказчика и
   подрядчика. Реестр листов и его изоляция по подрядчику живут на сервере
   (`vendor__scope` считается с условием «лист выпущен»), поэтому обеим сторонам
   достаётся один и тот же запрос, а не два похожих. */
/** Подписи степеней согласия листа CRS. Коды — из бланка заказчика. */
export const AGREED_RU: Record<string, string> = {
  '1': 'учтём полностью', '2': 'учтём частично', '3': 'отклонено',
}
export const AGREED_EN: Record<string, string> = {
  '1': 'fully implemented', '2': 'partly implemented', '3': 'not implemented',
}

export function useCrsSheets(tab: string, focus?: string, initialVendor?: string | null) {
  const [q, setQ] = useState('')
  const [disc, setDisc] = useState('')
  /* Отбор по подрядчику: «покажи все листы по Гидромашу к четвергу» —
     недельный вопрос ТДО, и до появления колонки он не задавался никак.
     С экрана организаций сюда приходят уже с выбранным подрядчиком: цифра
     «Открытых CRS» там была некликабельной, и отбор ставили руками. */
  const [vendor, setVendor] = useState(initialVendor ?? '')
  useEffect(() => { setVendor(initialVendor ?? '') }, [initialVendor])
  const [openDocs, setOpenDocs] = useState<Record<string, boolean>>({})
  const [activeSheet, setActiveSheet] = useState<string>('')

  const [page, setPage] = useState(0)

  /* Отбор, счёт и страница — на сервере. Прежде полка брала первые двести
     листов и всё считала по ним: на проекте, где листов больше, вкладки
     показывали числа «сколько поместилось», а выгрузка — тот же обрубок.

     Вкладка отбирает по состоянию ЗАМЕЧАНИЙ листа, а не по состоянию листа:
     человек ищет «где от меня ещё ждут», а не «что уже напечатано». */
  const narrow = { ...(disc ? { disc: [disc] } : {}), ...(vendor ? { vendor: [vendor] } : {}) }
  /* Три состояния листа вместо двух. «Отработан» отвечал на вопрос подрядчика
     («от меня больше ничего не ждут?») и смешивал два answered-состояния
     заказчика: утверждающий листал общую вкладку, выискивая глазами, где уже
     есть ответы и осталось только закрыть. Теперь:
       Ждут ответа — есть открытые замечания;
       Отвечены    — открытых нет, но есть ответы без решения (закрыть!);
       Закрыты     — не осталось ни открытых, ни ждущих решения: каждое
                     замечание либо ЗАКРЫТО, либо СНЯТО — решение заказчика. */
  const tabFilter: Record<string, string[]> =
    tab === 'open' ? { settled: ['false'] }
    : tab === 'answered' ? { settled: ['true'], toDecide: ['true'] }
    : tab === 'closed' ? { settled: ['true'], toDecide: ['false'] }
    : {}
  const query = useList<CrsSheetRow>('crs-sheets', {
    page,
    pageSize: PAGE_SIZE,
    sort: [{ id: 'id', desc: true }],
    filters: { ...narrow, ...tabFilter },
    q: q || undefined,
  })
  /* Счётчики вкладок — это total тех же запросов, а не длина загруженной
     страницы: строка нужна одна, число берётся из ответа. */
  const openCount = useList<CrsSheetRow>('crs-sheets',
    { page: 0, pageSize: 1, sort: [{ id: 'id', desc: true }], filters: { ...narrow, settled: ['false'] }, q: q || undefined })
  const answeredCount = useList<CrsSheetRow>('crs-sheets',
    { page: 0, pageSize: 1, sort: [{ id: 'id', desc: true }], filters: { ...narrow, settled: ['true'], toDecide: ['true'] }, q: q || undefined })
  const closedCount = useList<CrsSheetRow>('crs-sheets',
    { page: 0, pageSize: 1, sort: [{ id: 'id', desc: true }], filters: { ...narrow, settled: ['true'], toDecide: ['false'] }, q: q || undefined })
  /* Пустой массив создаётся заново на каждую отрисовку и тянет за собой
     пересчёт всего, что от него зависит: закрепляем ссылку. */
  const rows = query.data?.rows
  const sheets = useMemo(() => rows ?? [], [rows])
  const allSheets = sheets

  /* Метку фокуса ставит предыдущий экран — по номеру замечания. Лист, в котором
     оно лежит, выясняется по самому замечанию: на своей странице списка оно
     может и не оказаться. */
  const focused = useItem<Crs>('crs', focus || null).data
  useEffect(() => {
    if (focused?.sheet) { setActiveSheet(focused.sheet); setOpenDocs((s) => ({ ...s, [focused.doc]: true })) }
  }, [focused])

  /**
   * Лист замечания, на которое пришли по ссылке, — ОТДЕЛЬНЫМ запросом по коду.
   *
   * <p>Полка листов отбирается вкладкой, фильтрами и страницей, и фокус на этот
   * отбор не влияет никак. Значит лист, в который пришли, в загруженной странице
   * запросто не окажется — и панель молча оставалась пустой: ни листа, ни
   * ошибки, ни объяснения.
   *
   * <p>Хуже того, для извещения «подрядчик ответил» это не случайность, а
   * ПРАВИЛО. Ответ подрядчика уводит замечание из «открытых» той же командой,
   * которая выпускает извещение; лист, где открытых больше не осталось,
   * становится «улаженным» и выпадает из вкладки по умолчанию. То есть ссылка
   * умирала ровно в тот момент, когда её создавали, а при ответе целым бланком
   * — сразу по всему листу.
   *
   * <p>Отбор при этом НЕ ломается: лист берётся по коду и подставляется только
   * тогда, когда в отобранных его нет. Изоляция та же — сервер применяет её к
   * любой выборке реестра.
   *
   * <p>Берётся СПИСКОМ с отбором по коду, а не карточкой листа. У карточки
   * форма ДРУГАЯ: она отдаёт `items` массивом самих замечаний, а список —
   * их числом. Панель печатает счётчик, и массив объектов на его месте роняет
   * отрисовку целиком — белый экран вместо страницы. Одна форма на оба пути
   * такой ошибки не допускает вовсе.
   */
  /* Фокус бывает двух видов: код ЗАМЕЧАНИЯ (ссылки из уведомлений и карточек)
     и код ЛИСТА (ссылки с экрана рассмотрения). Разводятся сами: замечание
     находится запросом выше, и лист берётся его полем; не нашлось — сам фокус
     и есть код листа, пробуем его. Промах обеих веток безвреден: пустой ответ. */
  const sheetLookup = focused?.sheet ?? (focus && !focused ? focus : null)
  const focusedSheetQuery = useList<CrsSheetRow>('crs-sheets', {
    page: 0, pageSize: 1, sort: [{ id: 'id', desc: true }],
    filters: sheetLookup ? { id: [sheetLookup] } : { id: ['\u0000'] },
  }, { enabled: !!sheetLookup })
  const focusedSheet = sheetLookup ? focusedSheetQuery.data?.rows[0] : undefined
  /* Пришли по коду листа — открываем его так же, как лист замечания. */
  useEffect(() => {
    if (focusedSheet && focusedSheet.id === focus) {
      setActiveSheet(focusedSheet.id)
      setOpenDocs((s) => ({ ...s, [focusedSheet.doc]: true }))
    }
  }, [focusedSheet, focus])

  // Первый лист выбирается только когда данные пришли: набор бывает пустым.
  useEffect(() => {
    if (activeSheet || !sheets.length) return
    const first = sheets[0]!
    setActiveSheet(first.id)
    setOpenDocs((s) => ({ ...s, [first.doc]: true }))
  }, [activeSheet, sheets])

  const byDoc = useMemo(() => {
    const m = new Map<string, CrsSheetRow[]>()
    for (const s of sheets) m.set(s.doc, [...(m.get(s.doc) ?? []), s])
    /* Лист, в который пришли по ссылке, вклеивается в дерево, если отбор его не
       содержит. Иначе панель показывает лист, которого в полке слева нет, и
       человек не понимает, где он оказался. */
    if (focusedSheet && !m.get(focusedSheet.doc)?.some((s) => s.id === focusedSheet.id)) {
      m.set(focusedSheet.doc, [...(m.get(focusedSheet.doc) ?? []), focusedSheet])
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [sheets, focusedSheet])

  return {
    query,
    byDoc,
    /* Отобранные вкладкой и фильтром листы — их же выгружает кнопка в шапке:
       в файл обязано уйти то, что человек видит, а не весь набор. */
    sheets,
    /* Промах по загруженной странице — не «листа нет», а «его сюда не
       отбирали». Тогда берётся лист, добытый по коду замечания. */
    sheet: allSheets.find((s) => s.id === activeSheet)
      ?? (focusedSheet && focusedSheet.id === activeSheet ? focusedSheet : null),
    activeSheet,
    pick: setActiveSheet,
    q,
    // Смена условий сбрасывает страницу: на второй странице нового набора
    // легко оказаться за его пределами.
    setQ: (value: string) => { setQ(value); setPage(0) },
    disc,
    setDisc: (value: string) => { setDisc(value); setPage(0) },
    vendor,
    setVendor: (value: string) => { setVendor(value); setPage(0) },
    openDocs, setOpenDocs,
    page,
    setPage,
    pageCount: query.data?.pageCount ?? 1,
    total: query.data?.total ?? 0,
    counts: {
      open: openCount.data?.total ?? 0,
      answered: answeredCount.data?.total ?? 0,
      closed: closedCount.data?.total ?? 0,
      /* Сумма честная: три состояния не пересекаются и покрывают всё. */
      all: (openCount.data?.total ?? 0) + (answeredCount.data?.total ?? 0) + (closedCount.data?.total ?? 0),
    },
  }
}

/** Вкладки листов. Отбор идёт по состоянию ЗАМЕЧАНИЙ, а не по печати листа. */
export function crsSheetTabs(lang: Lang, counts: { open: number; answered: number; closed: number; all: number }) {
  return [
    { id: 'open', label: t(lang, 'Ждут ответа', 'Awaiting answer'), count: counts.open },
    { id: 'answered', label: t(lang, 'Отвечены', 'Answered'), count: counts.answered },
    { id: 'closed', label: t(lang, 'Закрыты', 'Closed'), count: counts.closed },
    { id: 'all', label: t(lang, 'Все листы', 'All sheets'), count: counts.all },
  ]
}

/** Левая полка: документ, под ним его листы CRS. */
export function CrsSheetRail({ lang, state }: { lang: Lang; state: ReturnType<typeof useCrsSheets> }) {
  const { query, byDoc, activeSheet, pick, q, setQ, disc, setDisc, vendor, setVendor, openDocs, setOpenDocs,
          page, setPage, pageCount, total } = state
  const discRows = useDictRows('disc')
  /* Список организаций — тот же реестр контрагентов, что на экране
     администрирования: второй источник разошёлся бы при первом переименовании. */
  const vendorRows = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
    .data?.rows ?? []
  return (
    <div style={{ width: railWidth(360), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 12px 0' }}>
        <Select size="sm" value={disc} onChange={(e) => setDisc(e.target.value)}>
          <option value="">{t(lang, 'Все дисциплины', 'All disciplines')}</option>
          {discRows.map((r) => (
            <option key={r.code} value={r.code}>{r.code} — {r.name}</option>
          ))}
        </Select>
        <Select size="sm" value={vendor} onChange={(e) => setVendor(e.target.value)} style={{ marginTop: 8 }}>
          <option value="">{t(lang, 'Все подрядчики', 'All contractors')}</option>
          {vendorRows.map((v) => (
            <option key={v.code} value={v.name}>{v.name}</option>
          ))}
        </Select>
      </div>
      <Toolbar style={{ padding: '8px 12px 10px' }}>
        <div style={{ flex: 1 }}>
          <Input size="sm" value={q} onChange={(e) => setQ(e.target.value)} leading={<Icon name="search" size={14} />}
                 placeholder={t(lang, 'Документ или номер листа…', 'Document or sheet number…')} />
        </div>
      </Toolbar>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {query.isError ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--danger-ink)' }}>
            {t(lang, 'Не удалось загрузить листы', 'Failed to load the sheets')}
          </div>
        ) : byDoc.length ? byDoc.map(([docNo, docSheets]) => {
          const expanded = openDocs[docNo] ?? false
          const openItems = docSheets.reduce((n, s) => n + s.open, 0)
          return (
            <div key={docNo}>
              <button
                onClick={() => setOpenDocs((s) => ({ ...s, [docNo]: !expanded }))}
                className="tdms-rail"
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer' }}
              >
                <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{docNo}</span>
                  <span style={{ display: 'block', font: 'var(--font-caption)', color: 'var(--text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{docSheets[0]?.title ?? ''}</span>
                </span>
                <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                  {docSheets.length} {t(lang, 'л.', 'sh.')}
                </span>
                {openItems ? <Badge tone="danger" size="sm">{openItems}</Badge> : null}
              </button>
              {expanded ? docSheets.map((s) => (
                <button
                  key={s.id}
                  onClick={() => pick(s.id)}
                  className="tdms-rail"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '9px 12px 9px 30px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: s.id === activeSheet ? 'var(--accent-soft)' : 'transparent', boxShadow: s.id === activeSheet ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}
                >
                  <CodeChip code={s.code} size="sm" />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)' }}>{s.id}</span>
                  <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>рев. {revVer(s.rev, s.ver)}</span>
                  <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                    {s.open ? `${s.open} / ${s.items}` : s.items}
                  </span>
                  {s.status === 'draft' ? <Badge tone="warning" size="sm">{t(lang, 'черновик', 'draft')}</Badge> : null}
                </button>
              )) : null}
            </div>
          )
        }) : (
          <div style={{ padding: '32px 16px', textAlign: 'center', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {query.isPending
              /* Спиннер к тексту: голая надпись неотличима от зависания. */
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</span>
              : t(lang, 'Листов по заданным условиям нет', 'No sheets match the filters')}
          </div>
        )}
      </div>
      {/* Полка постраничная: листов на проекте больше, чем помещается на
          экране, и «первые двести» означали бы, что остальных нет. */}
      {pageCount > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border-subtle)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{total}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Button variant="secondary" size="sm" disabled={page <= 0} onClick={() => setPage(page - 1)}>{t(lang, 'Назад', 'Prev')}</Button>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{page + 1} / {pageCount}</span>
            <Button variant="secondary" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>{t(lang, 'Вперёд', 'Next')}</Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** Замечания выбранного листа. Одна выборка на обе стороны. */
export function useSheetItems(sheetId: string) {
  return useList<Crs>('crs', {
    page: 0,
    pageSize: PAGE_SIZE,
    sort: [{ id: 'id', desc: false }],
    filters: { sheet: [sheetId] },
  })
}

/* Экран строится сверху вниз: документ → его листы CRS → замечания листа.
   Прежде здесь лежал плоский список замечаний, и лист как сущность на экране
   отсутствовал вовсе — оттого и читалось, будто одно замечание и есть CRS.
   Лист выпускается на документ И РЕВИЗИЮ, поэтому у документа их несколько:
   по листу на каждый круг рассмотрения. */
/* Построчный реестр замечаний по проекту.

   Кнопка «Реестр замечаний» выгружала строку на ЛИСТ: сколько в нём замечаний
   и сколько открытых. С этим на совещание не придёшь — там разбирают
   замечания, а не листы. Набор берётся с сервера порциями по потолку страницы:
   запрос «дай сразу все» молча вернул бы первые двести. */
async function exportComments(disc: string, q: string, lang: Lang): Promise<void> {
  const filters: Record<string, string[]> = disc ? { disc: [disc] } : {}
  const rows: Crs[] = []
  let page = 0
  let pageCount = 1
  do {
    const chunk = await fetchList<Crs>('crs', {
      page, pageSize: 200, sort: [{ id: 'id', desc: false }], filters, q: q || undefined,
    })
    rows.push(...chunk.rows)
    pageCount = chunk.pageCount
    page += 1
  } while (page < pageCount)

  exportRows<Crs>(rows, [
    ['Замечание', (c) => c.id],
    ['Документ', (c) => c.doc],
    ['Ревизия', (c) => c.rev],
    ['Подача', (c) => (c.ver == null ? '' : c.ver)],
    ['Лист', (c) => c.page],
    ['Дисциплина', (c) => c.disc],
    ['Вид', (c) => c.kind ?? ''],
    ['Критичность', (c) => c.crit],
    ['Пункт НТД', (c) => c.clause ?? ''],
    ['Текст замечания', (c) => c.text],
    ['Текст (EN)', (c) => c.textEn ?? ''],
    ['Автор', (c) => c.author],
    ['Выпущено', (c) => c.date],
    ['Срок ответа', (c) => c.due],
    ['Состояние', (c) => c.status],
    ['Ответ подрядчика', (c) => c.acsr?.text ?? ''],
    ['Согласие', (c) => c.acsr?.agreed ?? ''],
    ['Дата ответа', (c) => c.acsr?.date ?? ''],
    ['Решение', (c) => c.finalDecision ?? ''],
    ['Проверено на ревизии', (c) => c.fixedRev ?? ''],
    ['Лист CRS', (c) => c.sheet ?? ''],
  ], 'crs-comments.xlsx', 'CRS', lang)
}

export function CrsList({ lang, focus, vendor, onOpenViewer }: { lang: Lang; focus?: string; vendor?: string | null; onOpenViewer: (doc: string, crs?: string) => void }) {
  const [tab, setTab] = useState('open')
  const [printing, setPrinting] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const state = useCrsSheets(tab, focus, vendor)
  const { sheet, sheets, counts } = state
  const me = useMe()

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={
          <span>
            {me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> CRS / ACSR
          </span>
        }
        title={t(lang, 'Замечания CRS и ответы ACSR', 'Comments (CRS) & responses (ACSR)')}
        count={counts.all}
        actions={
          /* Выгружается то, что на экране: отобранные вкладкой и фильтром
             листы. Подкачивать остальное ради файла значит положить в него
             то, чего человек не видел.

             Кнопки выпуска здесь нет: замечание рождается внутри рассмотрения,
             потому что обязано лечь в лист, а лист собирается по его итогам. */
          <>
          {/* Построчный реестр замечаний — то, с чем идут на совещание: одна
              строка на замечание, а не на лист. Берётся с сервера целиком по
              текущему отбору, а не из загруженной полки. */}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />}
                  disabled={exporting}
                  title={t(lang, 'Все замечания отбора построчно: документ, текст, ответ, решение',
                                'Every comment of the selection: document, text, answer, decision')}
                  onClick={() => {
                    setExporting(true)
                    exportComments(state.disc, state.q, lang)
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
                        text: e instanceof Error ? e.message : String(e) }))
                      .finally(() => setExporting(false))
                  }}>
            {exporting ? t(lang, 'Собираем…', 'Building…') : t(lang, 'Замечания построчно', 'Comments by row')}
          </Button>
          <Button variant="secondary" size="sm" disabled={sheets.length === 0}
                  title={sheets.length === 0 ? t(lang, 'Выгружать нечего', 'Nothing to export') : undefined}
                  iconLeft={<Icon name="download" size={14} />}
                  onClick={() => exportRows<CrsSheetRow>(sheets, [
                    ['Лист CRS', (s) => s.id],
                    ['Документ', (s) => s.doc],
                    ['Наименование', (s) => s.title ?? ''],
                    ['Дисциплина', (s) => s.disc ?? ''],
                    ['Ревизия', (s) => s.rev],
                    ['Подача', (s) => (s.ver == null ? '' : s.ver)],
                    ['Код', (s) => s.code],
                    ['Состояние листа', (s) => (s.status === 'issued' ? t(lang, 'Выпущен', 'Issued') : t(lang, 'Черновик', 'Draft'))],
                    ['Замечаний', (s) => s.items],
                    ['Из них открытых', (s) => s.open],
                    ['Входящий TRM', (s) => s.trmIn ?? ''],
                    ['Возвратный TRM', (s) => s.trmOut ?? ''],
                    ['Подготовил', (s) => s.preparedBy],
                    ['Дата', (s) => s.date ?? ''],
                  ], 'crs-sheets.xlsx', 'CRS', lang)}>
            {t(lang, 'Реестр листов', 'Sheet register')}
          </Button>
          </>
        }
        tabsNode={<TabStrip value={tab} onChange={setTab} tabs={crsSheetTabs(lang, counts)} />}
      />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Документы и их листы */}
        <CrsSheetRail lang={lang} state={state} />
        {/* Лист и его замечания */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 24 }}>
          {sheet ? (
            <SheetPane sheet={sheet} lang={lang} onOpenViewer={onOpenViewer} onPrint={() => setPrinting(sheet.id)} focus={focus} />
          ) : (
            <EmptyState icon="message-square" title={t(lang, 'Выберите лист замечаний', 'Pick a comment sheet')} />
          )}
        </div>
      </div>
      <SheetPrint code={printing} lang={lang} onClose={() => setPrinting(null)} />
    </div>
  )
}

/* Лист: шапка и перечень вошедших замечаний. Замечание раскрывается в переписку
   на месте — уводить на отдельный экран ради двух реплик незачем. */
function SheetPane({ sheet, lang, onOpenViewer, onPrint, focus }: {
  sheet: CrsSheetRow
  lang: Lang
  onOpenViewer: (doc: string, crs?: string) => void
  onPrint: () => void
  focus?: string
}) {
  const [expanded, setExpanded] = useState<string>(focus ?? '')
  useEffect(() => { setExpanded(focus ?? '') }, [focus, sheet.id])
  /* Раскрыть мало — надо ещё показать. Лист бывает на два десятка позиций, и
     человек, пришедший по ссылке, видел шапку листа: раскрытое замечание
     оставалось ниже сгиба, и переход выглядел несработавшим.
     Прокрутка — после отрисовки строк, иначе прокручивать ещё нечего. */
  const focusRef = useRef<HTMLDivElement>(null)

  /* Замечания листа. Отбор идёт по полю строки, а не по клиентской фильтрации
     всего реестра: у листа своя выборка, и тянуть ради неё все замечания
     проекта в браузер нельзя. */
  const itemsQuery = useSheetItems(sheet.id)
  /* Пустой массив создаётся заново на каждую отрисовку: без закрепления ссылки
     эффект прокрутки срабатывал бы бесконечно. */
  const rows = itemsQuery.data?.rows
  const items = useMemo(() => rows ?? [], [rows])
  useEffect(() => {
    if (!focus || !items.some((c) => c.id === focus)) return
    /* Токены до JS-прокрутки не достают: «меньше движения» гасится руками. */
    focusRef.current?.scrollIntoView({ block: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }, [focus, items])
  /* Отвеченные замечания листа — то, по чему принимают решение пачкой. */
  const answered = items.filter((c) => c.status === 'answered').map((c) => c.id)
  const decide = useCommand<{ codes: string[] }, { done: number; rejected: number }>({
    path: () => '/crs:decide',
    body: (a) => ({ codes: a.codes, finalDecision: null }),
    invalidate: () => [qk.all('crs'), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <CodeChip code={sheet.code} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{sheet.id}</h2>
            {sheet.status === 'issued'
              ? <Badge tone="success" size="sm" dot>{t(lang, 'выпущен', 'issued')}</Badge>
              : <Badge tone="warning" size="sm" dot>{t(lang, 'черновик', 'draft')}</Badge>}
            <Button variant="secondary" size="sm" style={{ marginLeft: 'auto' }} iconLeft={<Icon name="printer" size={13} />} onClick={onPrint}>
              {t(lang, 'Печать', 'Print')}
            </Button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, padding: '12px 14px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
            <Field label={t(lang, 'Документ', 'Document')} mono>
              {/* Настоящий <a>: подчёркивание при наведении (base.css) работает
                  только на теге ссылки, span его не получает. href нет — переход
                  живёт в onOpenViewer, cursor задаётся руками. */}
              <a style={{ color: 'var(--text-link)', cursor: 'pointer' }} {...clickable(() => onOpenViewer(sheet.doc))}>{sheet.doc}</a>
            </Field>
            <Field label={t(lang, 'Ревизия и подача', 'Revision and submission')} mono>{revVer(sheet.rev, sheet.ver)}</Field>
            <Field label={t(lang, 'Дисциплина', 'Discipline')}>{sheet.disc ?? '—'}</Field>
            <Field label={t(lang, 'Рассмотрение', 'Review')} mono>{sheet.trmIn ?? '—'}</Field>
            <Field label={t(lang, 'Исходящий', 'Outgoing')} mono>{sheet.trmOut ?? '—'}</Field>
            <Field label={t(lang, 'Подготовил', 'Prepared by')}>{sheet.preparedBy}</Field>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Замечания листа', 'Comments in the sheet')}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{sheet.items}</span>
        {sheet.open ? <Badge tone="danger" size="sm">{t(lang, 'без ответа', 'unanswered')} {sheet.open}</Badge> : null}
        {/* Решение по всем отвеченным замечаниям листа разом: двадцать
            одинаковых решений в двадцать приёмов — работа руками, а не
            решение. Непрошедшие попадают в отчёт, а не отменяют остальные. */}
        {answered.length ? (
          <Button variant="secondary" size="sm" style={{ marginLeft: 'auto' }} iconLeft={<Icon name="check-check" size={13} />}
                  disabled={decide.isPending}
                  title={t(lang, `Закрыть отвеченные замечания листа: ${answered.length}`,
                                `Close the answered comments of the sheet: ${answered.length}`)}
                  onClick={() => decide.mutate({ codes: answered }, {
                    onSuccess: (r) => toast({ tone: r.rejected ? 'info' : 'success',
                      title: t(lang, 'Замечания закрыты', 'Comments closed'),
                      text: t(lang, `закрыто ${r.done}, пропущено ${r.rejected}`, `closed ${r.done}, skipped ${r.rejected}`) }),
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не вышло', 'Failed'), text: e.message }),
                  })}>
            {t(lang, 'Закрыть отвеченные', 'Close answered')}
          </Button>
        ) : null}
      </div>

      {itemsQuery.isPending ? (
        /* Спиннер к тексту: голая надпись неотличима от зависания. */
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {t(lang, 'Загрузка…', 'Loading…')}
        </div>
      ) : items.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((c, i) => (
            <div key={c.id} ref={c.id === focus ? focusRef : undefined}>
              <CommentRow n={i + 1} crs={c} lang={lang} open={c.id === expanded}
                          onToggle={() => setExpanded(expanded === c.id ? '' : c.id)}
                          onOpenViewer={onOpenViewer} />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
          {t(lang, 'В листе нет замечаний', 'The sheet has no comments')}
        </div>
      )}
    </div>
  )
}

/** Позиция листа. Свёрнутая — строка, развёрнутая — переписка с подрядчиком. */
function CommentRow({ n, crs, lang, open, onToggle, onOpenViewer }: {
  n: number
  crs: Crs
  lang: Lang
  open: boolean
  onToggle: () => void
  onOpenViewer: (doc: string, crs?: string) => void
}) {
  const crit = CRIT[crs.crit] || CRIT.low
  return (
    /* Рамка переводится, а не прыгает: accent-line ↔ border-default меняются
       при каждом раскрытии, и мгновенная смена дёргает глаз. */
    <div style={{ border: '1px solid ' + (open ? 'var(--accent-line)' : 'var(--border-default)'), borderRadius: 'var(--radius-md)', background: 'var(--surface-card)', overflow: 'hidden', transition: 'border-color var(--duration-fast) var(--ease-standard)' }}>
      <button onClick={onToggle} className="tdms-rail"
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0, background: 'transparent', cursor: 'pointer' }}>
        <span style={{ display: 'grid', placeItems: 'center', width: 20, height: 20, flex: '0 0 auto', borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{n}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)' }}>{crs.id}</span>
        <Badge tone={crit.tone} size="sm">{crit.ru}</Badge>
        {crs.verdict === 'rejected' ? <Badge tone="neutral" size="sm">{t(lang, 'снято', 'rejected')}</Badge> : null}
        <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: open ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{crs.text}</span>
        <CrsStatus c={crs} />
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
      </button>
      {open ? (
        /* Переписка проявляется, а не возникает: высокий блок монтируется скачком
           и выталкивает соседей. Уход мгновенный намеренно — закрытие быстрее входа. */
        <div style={{ padding: '4px 14px 14px', borderTop: '1px solid var(--border-subtle)', animation: 'fade var(--duration-fast) var(--ease-out)' }}>
          <Thread crs={crs} lang={lang} onOpenViewer={onOpenViewer} />
        </div>
      ) : null}
    </div>
  )
}

function CrsStatus({ c }: { c: Crs }) {
  /* Снятое — состояние сервера, которого нет в типе списка: без отдельной
     ветки оно рисовалось бы как «Отвечено», то есть как ожидающее решения. */
  const status: string = c.status
  if (status === 'withdrawn')
    return (
      <Badge tone="neutral" size="sm" dot>
        Снято
      </Badge>
    )
  if (c.status === 'open')
    return (
      <Badge tone="danger" size="sm" dot>
        Открыто
      </Badge>
    )
  if (c.status === 'closed')
    return (
      <Badge tone="neutral" size="sm" dot>
        Закрыто
      </Badge>
    )
  return (
    <Badge tone="success" size="sm" dot>
      Отвечено
    </Badge>
  )
}

/* Расшифровки кодов A/B/C/D здесь больше нет: код принадлежит документу и
   показывается на листе, а не у каждого замечания. */

/* Команды по замечанию. Обе сбрасывают и сам ресурс, и документы: закрытие
   меняет счётчик замечаний в строке реестра, а его считает сервер. */
function useCrsCommands() {
  const closeCrs = useCommand<{ code: string; finalDecision?: string }>({
    path: (a) => `/crs/${encodeURIComponent(a.code)}/close`,
    body: (a) => ({ finalDecision: a.finalDecision ?? null }),
    invalidate: () => [qk.all('crs'), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  /* Причина возврата обязательна и доходит до подрядчика: раньше она уходила
     только в журнал аудита, а он подрядчику закрыт. */
  const rework = useCommand<{ code: string; reason: string }>({
    path: (a) => `/crs/${encodeURIComponent(a.code)}/return-for-rework`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [qk.all('crs'), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  /* Продление срока ответа. Срок ставился при выпуске листа по SLA и дальше
     не двигался ничем: оставалось смотреть, как замечание горит просрочкой по
     дате, о переносе которой стороны уже договорились на совещании. */
  const extend = useCommand<{ code: string; due: string; reason: string }>({
    method: 'PUT',
    path: (a) => `/crs/${encodeURIComponent(a.code)}/due`,
    body: (a) => ({ due: a.due, reason: a.reason }),
    invalidate: () => [qk.all('crs'), qk.all('audit')],
  })
  /* Снятие ошибочно выпущенного замечания. Не закрытие: закрытие означает
     «исправление проверено» и проставляет подрядчику «Принято» — исправление,
     которого он не делал. */
  const withdraw = useCommand<{ code: string; reason: string }>({
    path: (a) => `/crs/${encodeURIComponent(a.code)}/withdraw`,
    body: (a) => ({ reason: a.reason }),
    invalidate: () => [qk.all('crs'), qk.all('docs'), qk.all('vdocs'), qk.all('audit')],
  })
  return { closeCrs, rework, extend, withdraw,
           busy: closeCrs.isPending || rework.isPending || extend.isPending || withdraw.isPending }
}

function Thread({ crs, lang, onOpenViewer }: { crs: Crs; lang: Lang; onOpenViewer: (doc: string, crs?: string) => void }) {
  const { closeCrs, rework, extend, withdraw, busy } = useCrsCommands()
  /* Вся переписка по замечанию, а не последний ответ: круги пишутся с самого
     начала, но наружу до сих пор выходил только верхний. */
  const rounds = useSub<{ rounds: { round: number; author: string | null; date: string | null; text: string
                                    agreed: string | null; disposition: string | null }[] }>(
    `/crs/${encodeURIComponent(crs.id)}/rounds`)
  /* Финальное решение — колонка бланка CRS и последнее слово заказчика.
     Необязательное: бланк заполняет её, когда замечание менялось. */
  const [decision, setDecision] = useState('')
  const [reason, setReason] = useState('')
  /* Новый срок ответа и причина продления: причина уходит в журнал и в
     разговор с подрядчиком — «почему круг шёл два месяца» спрашивают потом. */
  const [newDue, setNewDue] = useState('')
  const [dueReason, setDueReason] = useState('')
  const [withdrawReason, setWithdrawReason] = useState('')
  /* Снимает сторона заказчика: подрядчик на замечание отвечает. То же условие
     держит сервер — кнопку можно обойти запросом. */
  const me = useMe()
  const internal = !me.data?.vendor
  const status: string = crs.status
  const crit = CRIT[crs.crit] || CRIT.low
  return (
    <div>
      {/* Шапки с номером и документом здесь нет: они на строке и на листе.
          Осталось то, что принадлежит самому замечанию. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '10px 0 14px' }}>
        {/* Адрес выпуска: ревизия и ПОДАЧА. Одной ревизии приходится
            несколько присылок, и по «рев. 00» нельзя было понять, на какую
            из них замечание — то есть отвечал подрядчик на него или ещё нет. */}
        <Field label={t(lang, 'Выпущено на', 'Raised on')} mono>{revVer(crs.rev, crs.ver)}</Field>
        <Field label={t(lang, 'Страница', 'Page')} mono>{crs.page || '—'}</Field>
        <Field label={t(lang, 'Пункт НТД', 'Clause')}>{crs.clause || '—'}</Field>
        <Field label={t(lang, 'Критичность', 'Criticality')}><Badge tone={crit.tone} size="sm">{crit.ru}</Badge></Field>
        <Field label={t(lang, 'Срок ответа', 'Response due')} mono><span style={{ color: 'var(--date-forecast)' }}>{crs.due}</span></Field>
      </div>

      {/* CRS message */}
      <Bubble side="owner" author={crs.author} role={t(lang, 'Заказчик · рецензент', 'Owner · reviewer')} date={crs.date} title="CRS" tone="--amber-400">
        {crs.text}
        {/* Строка реестра, к которой относится замечание пакета реестра:
            писалась с самого начала и нигде не показывалась. */}
        {crs.rowRef ? <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>{t(lang, 'строка реестра', 'register row')}: {crs.rowRef}</div> : null}
        {crs.textEn ? <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 6 }}>EN: {crs.textEn}</div> : null}
        {/* Выкопировка с разметкой, расчёт, выдержка из норматива: замечание
            почти никогда не исчерпывается фразой, и раньше всё это ходило
            почтой мимо системы. */}
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
          <CrsFilesZone code={crs.id} lang={lang} kind="comment" disabled={crs.status === 'closed'} />
        </div>
      </Bubble>
      {/* Почему замечание снято: без причины снятое неотличимо от
          исправленного — подрядчик видит исчезнувшую позицию и спрашивает о
          ней на совещании. */}
      {crs.withdrawReason ? (
        <div style={{ marginLeft: 44, marginTop: 8, padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Замечание снято', 'Comment withdrawn')}: {crs.withdrawReason}
        </div>
      ) : null}
      {/* Причина возврата ответа: подрядчик обязан знать, что не так. */}
      {crs.reworkReason ? (
        <div style={{ marginLeft: 44, marginTop: 8, padding: '8px 12px', border: '1px solid var(--warning-line, var(--border-default))', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--warning-ink)' }}>
          {t(lang, 'Возвращено на доработку', 'Returned for rework')}: {crs.reworkReason}
        </div>
      ) : null}

      {/* Круги переписки: прежде виден был только последний ответ — второй
          круг стирал первый, и спор «что обещали в марте» решать было нечем. */}
      {rounds.data && rounds.data.rounds.length > 1 ? (
        <div style={{ marginLeft: 44, marginTop: 10, display: 'grid', gap: 6 }}>
          <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Прошлые круги', 'Previous rounds')}
          </div>
          {rounds.data.rounds.slice(0, -1).map((r) => (
            <div key={r.round} style={{ padding: '8px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-subtle)' }}>{t(lang, 'круг', 'round')} {r.round}</span>
              {' · '}{r.date ?? ''}{r.author ? ' · ' + r.author : ''}
              {r.agreed ? ' · ' + t(lang, 'согласие', 'agreed') + ' ' + r.agreed : ''}
              <div style={{ color: 'var(--text-body)', marginTop: 2 }}>{r.text}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* ACSR response */}
      {crs.acsr ? (
        <Bubble
          side="vendor"
          author={crs.acsr.author}
          role={t(lang, 'Подрядчик · ответ', 'Contractor · response')}
          date={crs.acsr.date}
          title="ACSR"
          tone="--blue-500"
          disp={crs.acsr.disp}
        >
          {crs.acsr.text}
          {/* Колонки листа CRS: степень согласия, кто рассматривал у подрядчика
              и на что опирается ответ. Пустых строк не рисуем. */}
          {crs.acsr.agreed || crs.acsr.reviewer || crs.acsr.reference ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border-subtle)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {crs.acsr.agreed ? <span>{t(lang, 'Согласие', 'Agreed')}: <b style={{ color: 'var(--text-body)' }}>{crs.acsr.agreed}</b> — {t(lang, AGREED_RU[crs.acsr.agreed] ?? '', AGREED_EN[crs.acsr.agreed] ?? '')}</span> : null}
              {crs.acsr.reviewer ? <span>{t(lang, 'Рассматривал', 'Reviewer')}: <b style={{ color: 'var(--text-body)' }}>{crs.acsr.reviewer}</b></span> : null}
              {crs.acsr.reference ? <span>{t(lang, 'Основание', 'Ref')}: {crs.acsr.reference}</span> : null}
            </div>
          ) : null}
        </Bubble>
      ) : (
        <div
          style={{
            marginLeft: 44,
            padding: '14px 16px',
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-subtle)',
            font: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon name="clock" size={15} />
          {t(lang, 'Ожидается ответ подрядчика (ACSR)', 'Awaiting contractor response (ACSR)')}
        </div>
      )}

      {/* Решение по замечанию. Закрытое — окончательно: если нужно снова,
          выпускается новое замечание со ссылкой на прежнее (решение заказчика
          от 12.08.26). Поэтому кнопки «Переоткрыть» здесь больше нет.

          Двойная защита остаётся намеренно: без ответа подрядчика кнопка
          скрыта, и ту же проверку делает сервер — кнопку можно обойти запросом. */}
      <div style={{ marginTop: 20, marginLeft: 44, display: 'flex', alignItems: 'center', gap: 8, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
        {status === 'withdrawn' ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            <Icon name="x" size={16} />
            {t(lang, 'Замечание снято заказчиком: исправления по нему не ждут',
                     'Withdrawn by the owner: no correction is expected')}
          </span>
        ) : crs.status === 'closed' ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--success-ink)' }}>
            <Icon name="check-check" size={16} />
            {t(lang, 'Замечание закрыто и зафиксировано в аудите', 'Closed and recorded in the audit trail')}
            {crs.finalDecision ? <span style={{ color: 'var(--text-muted)' }}>· {t(lang, 'решение', 'decision')}: {crs.finalDecision}</span> : null}
          </span>
        ) : crs.acsr ? (
          <>
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{t(lang, 'Решение по ответу:', 'Disposition:')}</span>
            <Input value={decision} onChange={(e) => setDecision(e.target.value)} style={{ minWidth: 260 }}
                   placeholder={t(lang, 'Финальное решение (в бланк CRS)', 'Final decision (goes to the CRS blank)')} />
            <Button variant="primary" size="sm" iconLeft={<Icon name="check" size={14} />}
                    disabled={busy} onClick={() => closeCrs.mutate({ code: crs.id, finalDecision: decision.trim() || undefined })}>
              {t(lang, 'Закрыть замечание', 'Close comment')}
            </Button>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} style={{ minWidth: 220 }}
                   placeholder={t(lang, 'Причина возврата — уйдёт подрядчику', 'Reason — goes to the contractor')} />
            <Button variant="secondary" size="sm" iconLeft={<Icon name="rotate-ccw" size={14} />}
                    disabled={busy || !reason.trim()}
                    title={reason.trim() ? undefined : t(lang, 'Возврат без причины подрядчику не объясним', 'A return without a reason explains nothing')}
                    onClick={() => rework.mutate({ code: crs.id, reason: reason.trim() })}>
              {t(lang, 'Вернуть на доработку', 'Return for rework')}
            </Button>
          </>
        ) : (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              <Icon name="lock" size={13} />
              {t(lang, 'Закрытие недоступно до получения ACSR', 'Cannot close until ACSR is received')}
            </span>
            {/* Ответа ещё ждут — значит, срок ещё живой, и его продлевают.
                Отвеченное продлевать нечему: сервер откажет тем же условием. */}
            <DateInput value={newDue} onChange={setNewDue} minDate={new Date()} lang={lang} block={false}
                       size="sm" placeholder={t(lang, 'новый срок', 'new due')} />
            <Input value={dueReason} onChange={(e) => setDueReason(e.target.value)} style={{ minWidth: 220 }}
                   placeholder={t(lang, 'Причина продления — уйдёт в журнал', 'Reason — goes to the log')} />
            <Button variant="secondary" size="sm" iconLeft={<Icon name="calendar-days" size={14} />}
                    disabled={busy || !newDue || !dueReason.trim()}
                    title={newDue && dueReason.trim() ? undefined : t(lang, 'Нужны дата и причина', 'Date and reason are required')}
                    onClick={() => extend.mutate({ code: crs.id, due: newDue, reason: dueReason.trim() }, {
                      onSuccess: () => { setNewDue(''); setDueReason('')
                        toast({ tone: 'success', title: t(lang, 'Срок продлён', 'Deadline extended'), text: crs.id }) },
                      onError: (e: Error) => toast({ tone: 'danger', title: t(lang, 'Не продлён', 'Not extended'), text: e.message }),
                    })}>
              {t(lang, 'Продлить срок', 'Extend')}
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />} onClick={() => onOpenViewer(crs.doc, crs.id)} style={{ marginLeft: 'auto' }}>
              {t(lang, 'Открыть на документе', 'Open on sheet')}
            </Button>
          </>
        )}
      </div>

      {/* Снятие ошибочно выпущенного замечания: не тот документ, дубль второго
          рецензента, снято по итогам совещания. Отдельно от закрытия — закрытие
          означает «исправление проверено» и записывает подрядчику исправление,
          которого он не делал. */}
      {internal && status !== 'closed' && status !== 'withdrawn' ? (
        <div style={{ marginTop: 10, marginLeft: 44, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} style={{ minWidth: 260 }}
                 placeholder={t(lang, 'Причина снятия — замечание выпущено по ошибке', 'Reason — the comment was raised by mistake')} />
          <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={14} />}
                  disabled={busy || !withdrawReason.trim()}
                  title={withdrawReason.trim()
                    ? t(lang, 'Замечание закроется снятым: исправлением оно не считается', 'The comment closes as withdrawn: it does not count as a correction')
                    : t(lang, 'Снятие без причины неотличимо от потери замечания', 'A withdrawal without a reason looks like a lost comment')}
                  onClick={() => withdraw.mutate({ code: crs.id, reason: withdrawReason.trim() })}>
            {t(lang, 'Снять замечание', 'Withdraw comment')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function Bubble({
  author,
  role,
  date,
  title,
  tone,
  disp,
  children,
}: {
  side: string
  author: string
  role: string
  date: string
  title: string
  tone: string
  disp?: string
  children: ReactNode
}) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <Avatar name={author} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>{author}</span>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{role}</span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {disp ? <Badge tone={disp === 'Принято' ? 'success' : 'warning'} size="sm">{disp}</Badge> : null}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, color: `var(${tone})`, letterSpacing: '.06em' }}>{title}</span>
          </span>
        </div>
        <div
          style={{
            padding: '12px 14px',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderLeft: `3px solid var(${tone})`,
            borderRadius: 'var(--radius-md)',
            font: 'var(--font-body)',
            fontSize: 'var(--text-md)',
            color: 'var(--text-body)',
          }}
        >
          {children}
          <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{date}</div>
        </div>
      </div>
    </div>
  )
}
